import type { PropsWithChildren, ReactNode } from 'react'
import React, { useContext } from 'react'
import { MainStylesVars, StyleSheet } from '../stylesheet'
import { MainConfig } from '../chapito-static'
import type { SocialMediaLinks } from '../../../grab/types'
import { FontAwesomeIcon } from '@fortawesome/react-native-fontawesome'
import { isSocialIconKey } from '../../../services/social-icons-data'
import { getSocialIconXml } from '../../../services/social-utils'
import { resolveImageUrl } from './resource-image'

import type { ButtonType } from 'antd/es/button'
import { Badge, Button } from 'antd'
import { getExternalMapLink } from './map-embed'
import type { AppPath } from '../navigation'
import { Navigation, RouteParamNames } from '../navigation'
import type { IconDefinition } from '@fortawesome/free-solid-svg-icons'
import { faLocationArrow, faMapLocationDot } from '@fortawesome/free-solid-svg-icons'
import type { IconProp } from '@fortawesome/fontawesome-svg-core'

const SOCIAL_ICON_SIZE = 32

export const Card: React.FC<PropsWithChildren<{ style?: React.CSSProperties }>> = ({ children, style }) => (
  <div style={{ ...styles.card, ...style }}>{children}</div>
)

function IconBadge({ children, size = 'large', color }: { children: ReactNode; size?: 'small' | 'large'; color?: string }): JSX.Element {
  return (
    <span
      style={{
        padding: size === 'large' ? 8 : 4,
        paddingInline: size === 'large' ? 15 : 8,
        fontSize: size === 'large' ? '1.2em' : '0.8em',
        borderRadius: 9999,
        backgroundColor: MainStylesVars.colorBlockActionLight,
        color: color ?? MainStylesVars.colorBlockAction,
        fontFamily: MainStylesVars.fontText,
      }}
    >
      {children}
    </span>
  )
}

export function BadgeList({ labels, size = 'large', color }: { labels: string[]; size?: 'small' | 'large'; color?: string }): JSX.Element {
  return (
    <div
      style={{
        display: 'flex',
        flexWrap: 'wrap',
        gap: 5,
        marginBottom: 5,
      }}
    >
      {[...new Set(labels)].map((l) => (
        <IconBadge key={l} size={size} color={color}>
          {l}
        </IconBadge>
      ))}
    </div>
  )
}

type LibIcon = IconProp
export const Icon: React.FC<{ style?: React.CSSProperties; icon: LibIcon; color?: string }> = ({ style, icon, color }) => (
  /* @ts-expect-error FontAwesomeIconStyle does not handle verticalAlign */
  <FontAwesomeIcon icon={icon} style={{ ...styles.icon, ...style, color: color ?? styles.icon.color }} />
)

export const RoundIcon: React.FC<{ icon: LibIcon; style?: React.CSSProperties }> = ({ icon, style }) => (
  <Icon style={{ ...styles.roundIcon, ...style }} color={style?.color ?? MainStylesVars.colorTitle} icon={icon} />
)

export const IconText: React.FC<{ icon: IconDefinition; value: string; href?: string | undefined }> = ({ icon, value, href }) => {
  return (
    <div>
      <RoundIcon icon={icon} style={styles.artistLinkIcon} />
      {href ? (
        <Button href={href} type="text" style={styles.artistLinkButton}>
          {value}
        </Button>
      ) : (
        <span style={styles.artistLinkButton}>{value}</span>
      )}
    </div>
  )
}

export const HorizontalBar: React.FC<{ style?: React.CSSProperties }> = ({ style }) => <div style={{ ...styles.horizontalBar, ...style }} />

export const DropShadow: React.FC<{ style?: React.CSSProperties }> = ({ style }) => <div style={{ ...styles.dropShadow, ...style }} />

export const SocialsList: React.FC<{ socials: SocialMediaLinks }> = ({ socials }) => {
  const config = useContext(MainConfig)
  const monochromeColor = config.socialIconStyle === 'monochrome' ? config.socialIconColor ?? '#000000' : undefined

  return (
    <div style={styles.socialsList}>
      {Object.keys(socials)
        .filter(isSocialIconKey)
        .filter((linkName) => !!socials[linkName])
        .map((linkName) => {
          const customIcon = config.socialIconStyle === 'custom' ? config.customSocialIcons?.[linkName] : undefined
          return (
            <Button style={styles.socialLinkBtn} key={linkName} href={socials[linkName]} target="_blank" rel="noreferrer">
              {customIcon !== undefined ? (
                <img src={resolveImageUrl(customIcon, { square: false })} style={styles.socialIcon} alt={linkName} />
              ) : (
                <span style={styles.socialIcon} dangerouslySetInnerHTML={{ __html: getSocialIconXml(linkName, SOCIAL_ICON_SIZE, monochromeColor) }} />
              )}
            </Button>
          )
        })}
    </div>
  )
}

type IconBtnProps = {
  children: ReactNode | string
  icon?: LibIcon
  onClick?: () => void
  badge?: boolean
  link?: string
  type?: ButtonType
  style?: React.CSSProperties
  className?: string
}

export const IconBtn: React.FC<IconBtnProps> = ({ icon, children, onClick, badge, link, type, style, className }) => (
  <Button style={{ ...styles.iconButton, ...style }} onClick={onClick} type={type ?? 'primary'} href={link} target="_blank" className={className}>
    <Badge dot={badge} offset={[-7, 0]}>
      {icon && <Icon icon={icon} style={{ marginRight: 8, padding: 0 }} color={style?.color} />}
    </Badge>
    {children}
  </Button>
)

export const MapBtn: React.FC<{ position: { lng: number | string; lat: number | string }; locationName: string }> = ({ position, locationName }) => (
  <Button style={styles.mapBtn} href={getExternalMapLink(position)} target="_blank">
    <Icon icon={faMapLocationDot} style={{ width: 30, height: 30 }} color={MainStylesVars.colorBlockAction} />
    <div style={styles.mapBtnLocation}>{locationName}</div>
    <Icon style={styles.mapBtnIcon} color={MainStylesVars.colorBlockAction} icon={faLocationArrow} />
  </Button>
)

export const NavigationLink: React.FC<{ children: React.ReactElement; path?: AppPath; style?: React.CSSProperties }> = ({
  children,
  path,
  style,
}) => {
  const { setUrl, search } = useContext(Navigation)

  // keep all hash/search parameters but the ones affecting the route
  search.delete(RouteParamNames.guestRouteGuestId)
  search.delete(RouteParamNames.eventRouteEventId)
  if (path?.page === 'event') search.set(RouteParamNames.eventRouteEventId, path.eventId)
  else if (path?.page === 'guest') search.set(RouteParamNames.guestRouteGuestId, path.guestId)

  const targetUrl = `?${search.toString()}${window.location.hash}`

  const onClick: React.MouseEventHandler = (ev) => {
    ev.preventDefault()
    setUrl(targetUrl)
  }

  return path ? (
    // the href serves no functional purpose, it is there for accessibility
    <a href={targetUrl} onClick={onClick} style={{ textDecoration: 'none', color: 'inherit', ...style }} draggable={false}>
      {children}
    </a>
  ) : (
    children
  )
}

export function getNavigationPermalink(path: AppPath): string {
  return `${window.location.origin}${window.location.pathname}${window.location.hash}?${
    path.page === 'home'
      ? ''
      : path.page === 'event'
      ? `${RouteParamNames.eventRouteEventId}=${encodeURIComponent(path.eventId)}`
      : path.page === 'guest'
      ? `${RouteParamNames.guestRouteGuestId}=${encodeURIComponent(path.guestId)}`
      : ''
  }`
}

const styles = StyleSheet.create({
  card: {
    backgroundColor: MainStylesVars.colorBlockBackground,
    borderRadius: 10,
    overflow: 'hidden',
    padding: 20,
  },
  icon: {
    verticalAlign: 'middle',
    padding: 5,
    color: MainStylesVars.colorBlockBackground,
    boxSizing: 'content-box',
    outline: 'none',
  },

  iconButton: {
    textTransform: 'none',
    textDecoration: 'none',
    display: 'flex',
    alignItems: 'center',
    color: MainStylesVars.colorBlockBackground,
  },

  roundIcon: {
    borderRadius: '50%',
    border: `1px solid ${MainStylesVars.colorTitle}`,
    marginRight: 8,
    marginTop: 4,
    marginBottom: 4,
  },

  verticalBar: {
    height: '100%',
    width: 4,
    backgroundColor: MainStylesVars.colorBlockAction,
  },

  horizontalBar: {
    height: 0.5,
    backgroundColor: MainStylesVars.colorBlockAction,
    marginTop: 15,
    marginBottom: 15,
  },

  dropShadow: {
    background: 'linear-gradient(0deg, rgba(0,0,0,0.595098107602416) 18%, rgba(255,255,255,0) 100%)',
    height: '30%',
    width: '100%',
    position: 'absolute',
    bottom: 0,
  },

  socialsList: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: 12,
  },

  socialIcon: {
    display: 'block',
    width: SOCIAL_ICON_SIZE,
    height: SOCIAL_ICON_SIZE,
    objectFit: 'contain',
  },

  socialLinkBtn: {
    backgroundColor: 'white',
    padding: 8,
    height: 'auto',
    lineHeight: 0,
    border: 'none',
  },

  mapBtn: {
    display: 'flex',
    padding: 15,
    boxSizing: 'content-box',
    gap: 10,
    alignItems: 'center',
    borderColor: MainStylesVars.colorBlockAction,
    backgroundColor: 'transparent',
    color: MainStylesVars.colorBlockAction,
    textDecoration: 'none',
    fontSize: '.8em',
  },

  mapBtnLocation: {
    flexGrow: 1,
    textAlign: 'left',
    whiteSpace: 'break-spaces',
    textDecoration: 'none',
  },

  mapBtnIcon: {
    borderRadius: '50%',
    width: 20,
    height: 20,
  },

  artistLinkIcon: {
    color: MainStylesVars.colorBlockAction,
    borderColor: MainStylesVars.colorBlockAction,
  },

  artistLinkButton: {
    color: MainStylesVars.colorBlockAction,
    paddingLeft: 3,
    fontSize: 14,
  },
})
